import { Component, EventEmitter, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import * as moment from 'moment';
import { SelectItem } from 'primeng/api';
import { Observable } from 'rxjs';
import { filter, finalize, mergeMap, takeUntil } from 'rxjs/operators';
import { CustomerIndividualNameSuffixType, ICustomerIndividualNameSuffixType } from '../../../customer/models/enums/customer-individual-name-suffix-type.enum';
import { DriverBaseComponent } from '../../../driver/driver-base-component';
import { DriverMedicalCertificateWaiverType as DriverMedicalCertificateWaiverTypeEnum } from '../../../driver/models/enums/driver-medical-certificate-waiver-type.enum';
import { DriverMedicalExaminerSpecialtyCodeType } from '../../../driver/models/enums/driver-medical-examiner-specialty-code-type.enum';
import { MedicalExamCertificateDecisionType as MedicalExamCertificateDecisionTypeEnum } from '../../../driver/models/enums/medical-exam-certificate-decision-type.enum';
import { MedicalExamCertificateRestrictionType as MedicalExamCertificateRestrictionTypeEnum } from '../../../driver/models/enums/medical-exam-certificate-restriction-type.enum';
import { MedicalExamCertificateExemption } from '../../../driver/models/requirement/medical-exam-certificate-exemption.model';
import { MedicalExamCertificateRestrictionDetail } from '../../../driver/models/requirement/medical-exam-certificate-restriction-detail.model';
import { MedicalExamCertificateRestriction } from '../../../driver/models/requirement/medical-exam-certificate-restriction.model';
import { MedicalExamCertificateViewModel } from '../../../driver/models/requirement/medical-exam-certificate-view.model';
import { MedicalExamCertificateWaiver } from '../../../driver/models/requirement/medical-exam-certificate-waiver.model';
import { MedicalExamCertificate } from '../../../driver/models/requirement/medical-exam-certificate.model';
import { Jurisdiction } from '../../../foundation/models/jurisdiction.model';
import { ModalSize } from '../../../shared/services/modal/mvd-modal.enum';
import { MvdModalService } from '../../../shared/services/modal/mvd-modal.service';
import { MedicalExamCertificateDecisionType } from '../../models/medical-exam-certificate-decision-type.model';
import { MedicalReviewReportContext } from '../../models/medical-review-report-context.model';
import { MedicalReviewService } from '../../services/medical-review.service';
import { AdditionalRequirementsModalComponent, AdditionalRequirementsModalComponentContext } from '../additional-requirements/additional-requirements-modal.component';
import { DocumentSelectModalComponent, DocumentSelectModalContext } from '../document-select-modal/document-select-modal.component';
import { SelectConditionsComponent } from '../select-conditions/select-conditions.component';
import { DriverMedicalCertificateStatusType } from '@max/driver/models/enums/driver-medical-certificate-status-type.enum';

export class DriverMedicalCertificateDecisionTypeRestrictionTypeMap {
    /**
     *
     */
    constructor(public driverMedicalCertificateDecisionTypeId: number, public driverMedicalCertificateRestrictionTypeId: number, public active: boolean) {
    }
    public id: number;
}

@Component({
    selector: 'medical-exam-certificate',
    templateUrl: './medical-exam-certificate.html',
    inputs: [
        'customerNumberId',
        'reportId',
        'createNewReport',
        'getEligibility',
        'businessTransactionNumberId',
        'isReadOnly',
        'dialogRef'
    ],
    outputs: ['formValidity', 'isSaving', 'eligibility', 'waiverStatus', 'dateReminder']
})
export class MedicalExamCertificateComponent extends DriverBaseComponent {
    public restrictionTypes: SelectItem[];
    public filteredRestrictionTypes: SelectItem[];
    public jurisdictions: Jurisdiction[];
    public mecDecisionTypes: MedicalExamCertificateDecisionType[];
    public mecWaiverTypes: SelectItem[];
    public mecExemptionTypes: SelectItem[];
    public mecSpecialtyCodeTypes: DriverMedicalExaminerSpecialtyCodeType[];
    public form: UntypedFormGroup;
    public isSaving: EventEmitter<boolean> = new EventEmitter<boolean>();
    public formValidity: EventEmitter<boolean> = new EventEmitter<boolean>();
    public closeOuterModal: EventEmitter<any> = new EventEmitter<boolean>();
    public eligibility: EventEmitter<any> = new EventEmitter<any>();
    public waiverStatus: EventEmitter<string> = new EventEmitter<string>();
    public dateReminder: EventEmitter<string> = new EventEmitter<string>();
    public suffixTypes: ICustomerIndividualNameSuffixType[] = new CustomerIndividualNameSuffixType().GetActive();
    public customerNumberId: number;
    public reportId: number;
    public createNewReport: boolean;
    public isReadOnly: boolean;
    public getEligibility: boolean;
    public topOfStackMedicalExamCertificate: MedicalExamCertificateViewModel;
    public businessTransactionNumberId: number;
    public documentFilename: string;
    public dialogRef: any;
    public maximumSignatureDate: Date;
    public maximumExpirationDate: Date;
    public minimumExpirationDate: Date;

    // variable used to hold old value so when restrictionsChanged event fires, we know whether or not the spe restriction was selected in the first place when we decide to clear the spe dates
    public speRestrictionWasSelected = false;

    @ViewChild(SelectConditionsComponent, { static: true })
    public selectConditionsComponent: SelectConditionsComponent;

    private medicalExamCertificateId: number;
    private decisionTypeEnum = new MedicalExamCertificateDecisionTypeEnum();
    private restrictionTypeEnum = new MedicalExamCertificateRestrictionTypeEnum();
    private waiverTypeEnum = new DriverMedicalCertificateWaiverTypeEnum();
    private CDLMRPAdditionalRequirementsIncidentTypeId = 747;

    public driverMedicalCertificateDecisionTypeRestrictionTypeMaps: DriverMedicalCertificateDecisionTypeRestrictionTypeMap[] = [];

    constructor(
        private formBuilder: UntypedFormBuilder,
        private medicalReviewService: MedicalReviewService,
        private modalService: MvdModalService
    ) {
        super();
        this.initializeForm(formBuilder);
    }

    public ngOnInit() {
        this.medicalReviewService.getMedicalExamCertificateLookup()
            .pipe(mergeMap(r => {
                this.loadLookups(r);
                return this.medicalReviewService.getTopOfStackMedicalExamCertificate(this.customerNumberId);
            }))
            .subscribe(r => this.loadData(r));
    }

    // ====================================================================== Form Initialization ======================================================================

    /**
     * Creates the form
     * @param formBuilder The form builder
     */
    private initializeForm = (formBuilder: UntypedFormBuilder): void => {
        this.form = formBuilder.group({
            decisionTypeId: [null, Validators.compose([Validators.required, Validators.minLength(1)])],
            restrictionIds: [[]],
            waiverIds: [[]],
            waiverDetails: formBuilder.array([]),
            exemptionIds: [[]],
            exemptionDetails: formBuilder.array([]),
            examinerFirstName: [null, Validators.compose([Validators.required, Validators.maxLength(50), this.nameValidator])],
            examinerMiddleName: [null, Validators.compose([Validators.maxLength(50), this.nameValidator])],
            examinerLastName: [null, Validators.compose([Validators.required, Validators.maxLength(70), this.nameValidator])],
            examinerSuffix: [null],
            examinerLicenseNumber: [null, Validators.compose([Validators.required, Validators.maxLength(15)])],
            specialtyCodeTypeId: [null, Validators.required],
            examinerPhoneNumber: [null, Validators.compose([Validators.required, Validators.minLength(10), this.repeatingDigitsValidator])],
            examinerRegistryCode: [null, Validators.compose([Validators.required, this.registryCodeValidator])],
            issuingJurisdictionId: [null, Validators.required],
            signatureDate: [null, Validators.compose([Validators.required, this.signatureDateValidator])],
            speCertificateEffectiveDate: [''],
            speCertificateEndDate: [''],
            isLongForm: [false],
            isGrandfatherEligible: [false],
            conditions: formBuilder.array([]),
            expirationDate: ['', Validators.compose([this.expirationDateValidator])],
            documentId: [null],
            qualificationStatusDate : [null]
        });

        this.maximumSignatureDate = moment().toDate();
        this.setExpirationDateBoundaries(this.maximumSignatureDate);
        this.observeFormValueChanges();
    };

    /**
     * Subscribes to all necessary form changes
     */
    private observeFormValueChanges = (): void => {
        this.observe(this.form.get('signatureDate').valueChanges, this.setExpirationDateBoundaries);
        this.observe(this.form.get('decisionTypeId').valueChanges, this.decisionTypeChanged);
        this.observe(this.form.get('restrictionIds').valueChanges, this.restrictionsChanged);
        this.observe(this.form.get('waiverIds').valueChanges, this.waiverTypeChanged);
        this.observe(this.form.get('exemptionIds').valueChanges, this.exemptionTypeChanged);
        this.observe(this.form.statusChanges, status => {
            if (status == 'VALID') {
                this.formValidity.emit(true);
            } else if (status == 'INVALID') {
                this.formValidity.emit(false);
            }
        });
    };

    // ====================================================================== Form Setting ======================================================================

    /**
     * Subscribes to an observable with the given callback and ensures the subscription is appropriately unsubscribed.
     *
     * @param observable The observable to subscribe to
     * @param callback The callback to subsribe with
     */
    private observe = <T>(observable: Observable<T>, callback: (r: T) => void): void => {
        observable.pipe(takeUntil(this.componentDestroyed)).subscribe(callback);
    };

    /**
     * Loads lookups like jurisdiction, decision types, restriction types, etc
     * @param lookups lookups to poppulate types and maps
     */
    private loadLookups = (lookups: any): void => {
        this.jurisdictions = lookups.result.jurisdictions.filter(e => true == e.active).sort(e => e.sortOrder);
        this.mecDecisionTypes = lookups.result.decisionTypes.filter(e => true == e.active).sort(e => e.sortOrder);
        this.mecWaiverTypes = lookups.result.waiverTypes.filter(e => true == e.active).sort(e => e.sortOrder).map(e => <SelectItem> { value: e.id, label: e.displayValue });
        this.mecExemptionTypes = lookups.result.exemptionTypes.filter(e => true == e.active).sort(e => e.sortOrder).map(e => <SelectItem> { value: e.id, label: e.displayValue });
        this.mecSpecialtyCodeTypes = lookups.result.specialtyCodeTypes.filter(e => true == e.active).sort(e => e.sortOrder);
        this.restrictionTypes = lookups.result.restrictionTypes.filter(e => true == e.active).sort(e => e.sortOrder).map(e => <SelectItem> { value: e.id, label: e.displayValue });
        this.driverMedicalCertificateDecisionTypeRestrictionTypeMaps = lookups.result.driverMedicalCertificateDecisionTypeRestrictionTypeMaps;
    };

    /**
     * Loads the form with values from the Medical Exam Certificate retrieved from the database
     * @param medicalExamCertificate Medical Exam Certificate retrieved from the database
     */
    private loadData = (medicalExamCertificate: MedicalExamCertificateViewModel): void => {
        if (!!medicalExamCertificate) {
            // Top of stack is used to determine current medical exam certificate vs what is on the form
            this.topOfStackMedicalExamCertificate = medicalExamCertificate;
            this.emitStatusMessages(medicalExamCertificate);
        }

        if (this.reportId != null) {
            this.medicalReviewService.getMedicalExamCertificateByReportIdForModal(this.reportId)
                .subscribe(result => {
                    this.loadMedicalExamCertificate(result);
                });
        } else if (this.customerNumberId != null && !this.createNewReport) {
            if (!!medicalExamCertificate) {
                this.loadMedicalExamCertificate(medicalExamCertificate);
            }
        }
    };

    /**
     * Emits messages to the outer component that contains this component
     */
    public emitStatusMessages = (medicalExamCertificate: MedicalExamCertificateViewModel): void => {
        if (!!medicalExamCertificate && !!medicalExamCertificate.id) {
            this.eligibility.emit({ eligibilityStatus: medicalExamCertificate.meetsMedicalStandards, eligibilityMessage: medicalExamCertificate.medicalExamCertificateStatus });

            // Currently only show these messages if they're not creating a new report. This is because down the line,
            if (!this.createNewReport) {
                const restrictionDetails = this.getTopOfStackRestrictionDetails();

                // Only show messages if specifically waivers, exemptions, or spe restrictions exist.
                if (!!restrictionDetails && restrictionDetails.length > 0) {
                    const earliestExpiringRestriction = restrictionDetails[0];
                    let statusMessage: string;

                    if (restrictionDetails.some(w => !w.effectiveDate && !w.expirationDate)) {
                        this.dateReminder.emit('Previous MEC has SPE, Waiver or Exemptions that do not have associated dates. Consider providing dates.');
                    }

                    if (moment(earliestExpiringRestriction.expirationDate) < moment()) {
                        statusMessage = 'Customer\'s waiver, exemption or SPE is expired';
                    } else {
                        statusMessage = `Customer has waiver, exemption or SPE the earliest expiration date is ${moment(earliestExpiringRestriction.expirationDate).format('MM/DD/YYYY')}. Check all before saving new MEC.`;
                    }

                    this.waiverStatus.emit(statusMessage);
                }
            }
        }
    };

    /**
     * Gets restriction details of the top of stack Medical Exam Certificate. restriction details includes waivers or exemptions that are attached to a Medical Exam Certificate restriction
     */
    private getTopOfStackRestrictionDetails = (): MedicalExamCertificateRestrictionDetail[] => {
        // Get restrictions that are SPE, have an exemption, or waiver
        let restrictionDetails: MedicalExamCertificateRestrictionDetail[] = [];

        if (!!this.topOfStackMedicalExamCertificate) {
            restrictionDetails = this.topOfStackMedicalExamCertificate.restrictionDetails;
        }

        return restrictionDetails;
    };

    /**
     * Loads the form with the values from the Medical Exam Certificate retrieved from the database
     * @param model The Medical Exam Certificate
     */
    public loadMedicalExamCertificate = (model: any): void => {
        if (model != null) {
            this.reportId = model.customerIndividualMedicalReportId;
            this.medicalExamCertificateId = model.id;
            this.form.get('decisionTypeId').setValue(model.medicalExamCertificateDecisionTypeId);
            this.form.get('examinerFirstName').setValue(model.medicalExaminerFirstName);
            this.form.get('examinerMiddleName').setValue(model.medicalExaminerMiddleName);
            this.form.get('examinerLastName').setValue(model.medicalExaminerLastName);
            this.form.get('examinerSuffix').setValue(model.medicalExaminerSuffixTypeId);
            this.form.get('examinerLicenseNumber').setValue(model.medicalExaminerLicenseNumber);
            this.form.get('specialtyCodeTypeId').setValue(model.medicalExaminerSpecialtyCodeId);
            this.form.get('examinerPhoneNumber').setValue(model.medicalExaminerPhoneNumber);
            this.form.get('examinerRegistryCode').setValue(model.medicalExaminerRegistryCode);
            this.form.get('issuingJurisdictionId').setValue(model.medicalExamCertificateIssuingJurisdictionId);
            this.form.get('isLongForm').setValue(true == model.isLongMedicalExamForm);
            this.form.get('isGrandfatherEligible').setValue(true == model.isGrandfatherEligible);
            this.form.get('documentId').setValue(model.documentId);

            if (model.qualificationStatusDate != null) {
                let signatureDate = this.form.get('signatureDate');

                signatureDate.setValidators([
                    this.signatureDateValidator
                ]);

                signatureDate.updateValueAndValidity();
                this.form.get('qualificationStatusDate')?.setValue(model.qualificationStatusDate);
                this.form.get('qualificationStatusDate').updateValueAndValidity();
            }

            this.setFormDate(this.form, 'signatureDate', model.medicalExamCertificateSignatureDate);
            this.setFormDate(this.form, 'expirationDate', model.expirationDate);
            this.setFormDate(this.form, 'speCertificateEffectiveDate', model.speCertificateEffectiveDate);
            this.setFormDate(this.form, 'speCertificateEndDate', model.speCertificateEndDate);
            this.documentFilename = model.documentFilename;
            this.selectConditionsComponent.selectConditions(model.conditionIds);
            this.initializeRestrictionForms(model);

            // Disable all form fields if read only
            // If the MEC was created more than a day ago, disable everything but the expiration date
            // Note that this section must be after completely loading the form with the MEC
            // This is because form.disable/enable will fire off form subscriptions which could lead to unexpected behavior
            if (this.isReadOnly) {
                this.form.disable();
            } else if ((moment().diff(model.createdDate, 'hours') > 24) && model.id > 0) {
                this.form.disable();

                if (!this.isReadOnly) {
                    this.form.get('expirationDate').enable();
                }
            }
        }
    };

    /**
     * Properly formats a defined date and sets a provided date form control to it
     * @param controlName The form control name
     * @param dateValue The date to assign to the form control
     */
    private setFormDate = (form: UntypedFormGroup, controlName: string, dateValue: any): void => {
        const momentValue = moment(dateValue);

        if ((null == dateValue) || (true != momentValue.isValid())) {
            return;
        }

        const formControl = form.get(controlName);

        if (null != formControl) {
            formControl.setValue(momentValue.toDate());
        }
    };

    /**
     * Adds the restrictions from a provided medical exam certificate to the form
     * @param model The medical exam certificate that holds the restrictions
     */
    private initializeRestrictionForms(model: any) {
        if (!!model.restrictions && model.restrictions.length > 0) {
            const restrictionsControl = this.form.get('restrictionIds');

            // RestrictionTypeIds without duplicates
            let activeRestrictionTypeIdSet = new Set(model.restrictions.filter(r => r.active).map(r => r.restrictionTypeId));
            if (activeRestrictionTypeIdSet.has(this.restrictionTypeEnum.AccompaniedByASkillPerformanceExaminationCertificate.id)) {
                this.speRestrictionWasSelected = true;
            }

            restrictionsControl.setValue([...activeRestrictionTypeIdSet]);

            if (this.showExemptions() || this.showWaivers()) {
                model.restrictions.forEach(r => {
                    this.addRestrictionDetailsToForm('exemption', r);
                    this.addRestrictionDetailsToForm('waiver', r);
                });
            }
        }
    }

    /**
     * Adds a restrictionDetail (waiver/exemption) to the form
     * @param indicator the type of restrictionDetail, can be a waiver or exemption
     * @param restriction The restriction that holds the restriction detail
     */
    private addRestrictionDetailsToForm = (indicator: 'waiver' | 'exemption', restriction: MedicalExamCertificateRestriction): void => {
        const models = restriction[`${indicator}s`];
        if (!!models) {
            const idsControl = this.form.get(`${indicator}Ids`);
            const detailsControl = this.form.get(`${indicator}Details`) as UntypedFormArray;
            const currentIds = idsControl.value; // Waiver and Exemption type id's we currently have
            const ids: number[] = []; // Waiver and Exemption type id's

            for (const model of models) {
                const typeId = model[`${indicator}TypeId`];
                ids.push(typeId);

                const isQualifiedByOperations = model['waiverTypeId'] && model['waiverTypeId'] == this.waiverTypeEnum.QualifiedByOperation.id;
                const restrictionTypeDisplayValue = this.restrictionTypes.find(r => r.value == restriction.restrictionTypeId);
                const typesName = `mec${indicator[0].toUpperCase() + indicator.slice(1)}Types`;
                const type = this[typesName].find(t => t.value == typeId);

                const group = this.formBuilder.group({
                    id: [type.value],
                    displayValue: [type.label],
                    effectiveDate: [null],
                    expirationDate: [null],
                    restrictionTypeId: [restriction.restrictionTypeId],
                    restrictionTypeDisplayValue: [restrictionTypeDisplayValue ? restrictionTypeDisplayValue.label : ''],
                    active: [restriction.active]
                });

                this.setFormDate(group, 'effectiveDate', model.effectiveDate);
                this.setFormDate(group, 'expirationDate', model.expirationDate);

                if (isQualifiedByOperations) {
                    group.get('expirationDate').disable();
                    group.get('effectiveDate').setValidators([Validators.required]);
                }

                // If the restriction isn't active (historical) then disable so we don't modify history
                if (false == restriction.active) {
                    group.disable();
                }

                if (detailsControl) {
                    detailsControl.push(group);
                }
            }

            // Use Set to prevent duplicates
            idsControl.setValue([...new Set(ids.concat(currentIds))]);
        }
    };

    // ====================================================================== Form Value Subscriptions ======================================================================

    /**
     * Sets the boundary of the expiration date depending on the signature date chosen
     * @param signatureDate The signature date of the Medical Exam Certificate
     */
    private setExpirationDateBoundaries = (signatureDate: Date): void => {
        const signatureMoment = moment(signatureDate);

        if (signatureMoment.isValid()) {
            this.minimumExpirationDate = signatureMoment.toDate();
            this.maximumExpirationDate = signatureMoment.add(2, 'years').toDate();
        } else {
            // If there isn't a valid signature date we don't yet know the boundaries
            this.minimumExpirationDate = this.maximumExpirationDate = null;
        }

        this.form.get('expirationDate').updateValueAndValidity();
    };

    /**
     * Upon decision type change, filters out any incompatible restrictions from both the UI dropdown and from the list of selected restrictions
     * @param decisionTypeId The decision type chosen
     */
    private decisionTypeChanged = (decisionTypeId: number): void => {
        let restrictionIdForm = this.form.get('restrictionIds');

        // Get the allowed restriction types for the decision type chosen
        let allowedRestrictionTypes = this.driverMedicalCertificateDecisionTypeRestrictionTypeMaps.filter(m => m.driverMedicalCertificateDecisionTypeId == decisionTypeId);

        // Filter out restriction types that were previously chosen that are not allowed for the decision type chosen
        let filteredSelectedRestrictionIds = restrictionIdForm.value.filter(r => allowedRestrictionTypes.some(m => m.driverMedicalCertificateRestrictionTypeId == r));

        // Change list of allowed restriction types on UI dropdown
        this.filteredRestrictionTypes = this.restrictionTypes.filter(r => allowedRestrictionTypes.some(m => m.driverMedicalCertificateRestrictionTypeId == r.value));
        restrictionIdForm.setValue(filteredSelectedRestrictionIds);

        // Check restrictions now that the decision type has changed
        // This will remove any exemptions or waivers that should not be there
        this.restrictionsChanged(filteredSelectedRestrictionIds);
    };

    /**
     * Called when changing the selection on the UI dropdown as well as when the decision type is changed.
     * Will remove restrictionDetails (waiver/exemption) from form if a restriction type was deselected that includes restrictionDetails
     * @param selectedRestrictionIds The selected restriction types
     */
    public restrictionsChanged = (selectedRestrictionIds: number[]): void => {
        const waiverOrExemptionRestriction = selectedRestrictionIds.some(e => e == this.restrictionTypeEnum.AccompaniedbyWaiverExemption.id);
        const qualifiedByCFRRestriction = selectedRestrictionIds.some(e => e == this.restrictionTypeEnum.QualifiedByOperationOf49CFR391_64.id);
        const speRestriction = selectedRestrictionIds.some(e => e == this.restrictionTypeEnum.AccompaniedByASkillPerformanceExaminationCertificate.id);
        const qualifiedStateVariancesDecision = this.form.get('decisionTypeId').value == this.decisionTypeEnum.Qualified_StateVariances.id;

        if (!this.showWaivers()) {
            // Remove all waivers
            this.removeExemptionsOrWaiversFromForm('waiver', selectedRestrictionIds, true);
        } else if (!waiverOrExemptionRestriction || !qualifiedByCFRRestriction || !qualifiedStateVariancesDecision) {
            // We may need to remove waivers for just 1 of the restriction types
            this.removeExemptionsOrWaiversFromForm('waiver', selectedRestrictionIds, false);
        }

        if (!this.showExemptions()) {
            // Remove all exemptions
            this.removeExemptionsOrWaiversFromForm('exemption', selectedRestrictionIds, true);
        } else {
            // We had a bug where exemptions were incorrectly added to restriction type Qualified by operation of 49 CFR 391.64
            // Exemptions should only be attached to the Accompanied by Waiver/Exemption restriction type
            // This else clause is here to handle those cases and correctly remove that exemption when the user deselects that restriction type
            const details = this.form.get('exemptionDetails') as UntypedFormArray;

            if (!qualifiedByCFRRestriction && details.value.some(d => d.restrictionTypeId == this.restrictionTypeEnum.QualifiedByOperationOf49CFR391_64.id)) {
                this.removeExemptionsOrWaiversFromForm('exemption', selectedRestrictionIds, false);
            }
        }

        // If we deselect Accompanied By SPE, we need to null out the dates so that it doesn't get saved behind the scenes. We are unable to delete the dates on the UI otherwise
        if (speRestriction) {
            this.speRestrictionWasSelected = true;
        } else if (!speRestriction) {
            if (this.speRestrictionWasSelected) {
                this.form.get('speCertificateEffectiveDate').setValue(null);
                this.form.get('speCertificateEndDate').setValue(null);
            }

            this.speRestrictionWasSelected = false;
        }
    };

    /**
     * Removes waivers/exemptions from form
     * @param indicator whether the restriction detail is a waiver or an exemption
     * @param selectedRestrictionIds The selected restriction types
     * @param removeAll Whether we should remove all waiver/exemption types from the form
     */
    private removeExemptionsOrWaiversFromForm = (indicator: 'waiver' | 'exemption', selectedRestrictionIds: number[], removeAll: boolean): void => {
        const ids = this.form.get(`${indicator}Ids`);
        const details = this.form.get(`${indicator}Details`) as UntypedFormArray;

        if (removeAll) {
            ids.setValue([]);
            details.clear();
        } else {
            const decisionTypeId = this.form.get('decisionTypeId').value;
            let detailsToKeep = details.controls.filter(c => selectedRestrictionIds.includes(c.value.restrictionTypeId));

            if (indicator == 'waiver' && decisionTypeId && decisionTypeId != this.decisionTypeEnum.Qualified_StateVariances.id) {
                // When decision type is not State Variances we need to remove any waivers for Accompanied by Waiver/Exemption
                detailsToKeep = detailsToKeep.filter(c => c.value.restrictionTypeId != this.restrictionTypeEnum.AccompaniedbyWaiverExemption.id);
            }

            const idsToKeep = ids.value.filter(w => detailsToKeep.some(d => d.value.id == w));

            this.form.setControl(`${indicator}Details`, new UntypedFormArray(detailsToKeep));
            ids.setValue(idsToKeep);
        }
    };

    /**
     * Appropriately removes and adds waivers from the list of selected waivers
     * @param selectedWaiverIds waiver types that were selected
     */
    public waiverTypeChanged = (selectedWaiverIds: number[]): void => {
        const waiverDetails = this.form.get('waiverDetails') as UntypedFormArray;
        this.manageArray(selectedWaiverIds, waiverDetails, this.mecWaiverTypes);

        if (!!waiverDetails) {
            const index = waiverDetails.value.indexOf(w => w.id == this.waiverTypeEnum.QualifiedByOperation.id);
            if (index != -1) {
                let detailControl = waiverDetails.value.at(index);
                detailControl.get('expirationDate').disable();
                detailControl.get('effectiveDate').setValidators([Validators.required]);
            }
        }
    };

    /**
     * Appropriately removes and adds exemptions from the list of selected exemptions
     * @param selectedExemptionIds exemption types that were selected
     */
    public exemptionTypeChanged = (selectedExemptionIds: number[]): void => {
        this.manageArray(selectedExemptionIds, this.form.get('exemptionDetails') as UntypedFormArray, this.mecExemptionTypes);
    };

    /**
     * Appropriately removes and adds waivers/exemptions from the list of selected waivers/exemptions
     * @param selectedIds selected waiver/exemption types
     * @param detail waiver/exemption form control
     * @param typeOptions waiver/exemption options to populate UI dropdown
     */
    private manageArray = (selectedIds: number[], detail: any, typeOptions: SelectItem[]): void => {
        const detailArray = detail.controls;
        const detailsToRemove = detailArray.filter(d => false == selectedIds.some(i => i == d.value.id));
        const idsToRemove = detailsToRemove && detailsToRemove.length > 0 && detailsToRemove.map(d => d.value.id);
        const idsToAdd = selectedIds.filter(i => false == detailArray.some(d => d.value.id == i));

        // Remove ids that have been deselected
        if (!!idsToRemove && idsToRemove.length > 0) {
            idsToRemove.forEach(i => {
                const index = detailArray.map(d => d.value.id).indexOf(i);
                // Remove at index via splice
                detailArray.splice(index, 1);
            });
        }

        // Add ids that do not currently exist in the detail array
        idsToAdd.forEach(i => {
            const type = typeOptions.find(t => t.value == i);
            if (!!type) {
                const grp = this.formBuilder.group({
                    id: [type.value],
                    displayValue: [type.label],
                    effectiveDate: [null, Validators.compose([Validators.required])],
                    expirationDate: [null, Validators.compose([Validators.required])],
                    restrictionTypeDisplayValue: [''],
                    active: [true]
                }, {
                    validators: [this.expirationAfterEffectiveValidator]
                });

                grp.get('effectiveDate').valueChanges.subscribe(() => {
                    grp.updateValueAndValidity({ onlySelf: true });
                });
                grp.get('expirationDate').valueChanges.subscribe(() => {
                    grp.updateValueAndValidity({ onlySelf: true });
                });

                (detail as UntypedFormArray).push(grp);

                (detail as UntypedFormArray).updateValueAndValidity();
                this.form.updateValueAndValidity();
            }
        });
    };

    // User Story 159660
    private expirationAfterEffectiveValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
        const effective = group.get('effectiveDate')?.value;
        const expiration = group.get('expirationDate')?.value;

        const today = moment().startOf('day');
        const errors: any = {};

        if (effective && moment(effective).isAfter(today)) {
            errors.effectiveDateTooLarge = true;
        }

        if (effective && expiration && moment(expiration).isBefore(moment(effective))) {
            errors.expirationDateTooSmall = true;
        }

        return Object.keys(errors).length ? errors : null;
    };

    // ====================================================================== ngIf expressions ======================================================================

    /**
     * Returns true if the exemptions select menu should be displayed.
     *
     * Display when:
     *   Decision type Qualified - Non Excepted Interstate is selected and Restriction type Accompanied by Waiver/Exemption is selected.
     */
    public showExemptions = (): boolean => {
        const decisionTypeId = this.form.get('decisionTypeId').value;

        if (this.decisionTypeEnum.Qualified_NonExceptedInterstate.id == decisionTypeId) {
            const restrictionIds = this.form.get('restrictionIds').value;
            return restrictionIds.some(e => e == this.restrictionTypeEnum.AccompaniedbyWaiverExemption.id);
        }

        return false;
    };

    /**
     * Returns true if the waivers select menu should be displayed.
     *
     * Display when:
     *   Restriction type Qualified By Operation Of 49 CFR 391.64 is selected with any Decision type.
     *   OR
     *   Restriction type Accompanied by Waiver/Exemption is selected and Decision type Qualified - StateVariances is selected.
     */
    public showWaivers = (): boolean => {
        const decisionTypeId = this.form.get('decisionTypeId').value;
        const restrictionIds = this.form.get('restrictionIds').value;

        if (restrictionIds.some(e => e == this.restrictionTypeEnum.QualifiedByOperationOf49CFR391_64.id)) {
            return true;
        }

        if (this.decisionTypeEnum.Qualified_StateVariances.id == decisionTypeId) {
            return restrictionIds.some(e => e == this.restrictionTypeEnum.AccompaniedbyWaiverExemption.id);
        }

        return false;
    };

    /**
     * Returns true if any exemptions have been selected.
     */
    public hasExemptions = (): boolean => {
        const exemptionIds = this.form.get('exemptionIds');
        return !!exemptionIds && exemptionIds.value.length > 0;
    };

    /**
     * Returns true if any waivers have been selected.
     */
    public hasWaivers = (): boolean => {
        const waiverIds = this.form.get('waiverIds');
        return !!waiverIds && waiverIds.value.length > 0;
    };

    /**
     * Returns true if Grandfathered restriction was selected
     */
    public isGrandfatheredSelected = (): boolean => {
        const restrictionIds = this.form.get('restrictionIds').value;
        return restrictionIds.some(e => e == this.restrictionTypeEnum.GrandfatheredFromStateRequirements.id);
    };

    /**
     * Returns true if Accompanied by SPE restriction was selected
     */
    public showSpeEffectiveExpirationDates = (): boolean => {
        const restrictionIds = this.form.get('restrictionIds').value;
        return restrictionIds.some(e => e == this.restrictionTypeEnum.AccompaniedByASkillPerformanceExaminationCertificate.id);
    };

    // ====================================================================== Saving and Validation ======================================================================

    /**
     * Loads a Medical Exam Certificate model with form data and returns the save function to subscribe to
     * @param data The data containing a medical report to associated to, if provided
     * @returns The save observable to be subscribed to
     */
    public save = (data: MedicalReviewReportContext = null): Observable<any> => {
        this.isSaving.emit(true);
        const model = new MedicalExamCertificate();

        if (data != null) {
            model.customerIndividualMedicalReportId = data.reportId;
        } else {
            model.customerIndividualMedicalReportId = this.reportId;
        }

        if (this.topOfStackMedicalExamCertificate != null) {
            model.driverMedicalCertificateStatusTypeId = this.topOfStackMedicalExamCertificate.driverMedicalCertificateStatusTypeId;
        } else {
            model.driverMedicalCertificateStatusTypeId = new DriverMedicalCertificateStatusType().Active.id;
        }

        model.id = this.medicalExamCertificateId;
        model.confirmAdminAction = false;
        model.customerNumberId = this.customerNumberId;
        model.documentId = this.form.get('documentId').value;
        model.medicalExamCertificateDecisionTypeId = this.form.get('decisionTypeId').value;
        model.medicalExaminerFirstName = this.form.get('examinerFirstName').value;
        model.medicalExaminerMiddleName = this.form.get('examinerMiddleName').value;
        model.medicalExaminerLastName = this.form.get('examinerLastName').value;
        model.medicalExaminerSuffixTypeId = this.form.get('examinerSuffix').value;
        model.medicalExaminerLicenseNumber = this.form.get('examinerLicenseNumber').value;
        model.medicalExaminerSpecialtyCodeId = this.form.get('specialtyCodeTypeId').value;
        model.medicalExaminerPhoneNumber = this.form.get('examinerPhoneNumber').value;
        model.medicalExaminerRegistryCode = this.form.get('examinerRegistryCode').value;
        model.medicalExamCertificateIssuingJurisdictionId = this.form.get('issuingJurisdictionId').value;
        model.medicalExamCertificateSignatureDate = this.form.get('signatureDate').value;
        model.speCertificateEffectiveDate = this.form.get('speCertificateEffectiveDate').value;
        model.speCertificateEndDate = this.form.get('speCertificateEndDate').value;
        model.isLongMedicalExamForm = true == this.form.get('isLongForm').value;
        model.isGrandfatherEligible = true == this.form.get('isGrandfatherEligible').value;
        model.restrictions = [];
        model.conditionIds = this.selectConditionsComponent.getSelectedConditionIds();
        model.businessTransactionNumberId = this.businessTransactionNumberId;
        model.effectiveDate = this.form.get('signatureDate').value;
        model.expirationDate = this.form.get('expirationDate').value;
        model.qualificationStatusDate = this.form.get('qualificationStatusDate').value;

        if (model.medicalExaminerMiddleName === '') {
            // This is the only optional text type field and we should not be sending empty strings
            // If the user enters text and deletes it from the field it becomes an empty string
            // which causes an infinite validation loop because they can never make this field null again
            model.medicalExaminerMiddleName = null;
        }

        const restrictionIds = this.form.get('restrictionIds').value as UntypedFormArray;
        for (let i = 0; i < restrictionIds.length; i++) {
            this.applyRestrictionToCertificate(model, restrictionIds[i]);
        }

        return this.medicalReviewService.saveMedicalExamCertificate(model)
            .pipe(finalize(() => this.isSaving.emit(false)));
    };

    /**
     * Returns true if restriction passes validation
     */
    public validateMedicalExamCertificateRestrictions = (): boolean => {
        let isValid = true;
        const currentCertificateRestrictions = this.getTopOfStackRestrictionDetails();
        const selectedRestrictions = this.form.get('restrictionIds').value;
        const selectedWaivers = this.form.get('waiverIds').value;
        const selectedExemptions = this.form.get('exemptionIds').value;

        if (!this.medicalExamCertificateId && currentCertificateRestrictions && currentCertificateRestrictions.length > 0) {
            // Get waivers, exemptions, and spe that existed on the top of stack MEC but were not selected on this modal.
            const waiversMissing = currentCertificateRestrictions.filter(r => !!r.waiverTypeId && !selectedWaivers.some(w => w.id == r.waiverTypeId));
            const exemptionsMissing = currentCertificateRestrictions.filter(r => !!r.exemptionTypeId && !selectedExemptions.some(e => e.id == r.exemptionTypeId));
            const speMissing = currentCertificateRestrictions.filter(r =>
                r.restrictionTypeId == this.restrictionTypeEnum.AccompaniedByASkillPerformanceExaminationCertificate.id
                && !selectedRestrictions.some(rc => rc == this.restrictionTypeEnum.AccompaniedByASkillPerformanceExaminationCertificate.id)
            );

            const allRestrictions = waiversMissing.concat(exemptionsMissing, speMissing);
            isValid = this.hasExpiredRestriction(allRestrictions);
        }

        return isValid;
    };

    /**
     * Determines if any of the restrictions provided are expired
     * @param restrictions
     */
    private hasExpiredRestriction = (restrictions: MedicalExamCertificateRestrictionDetail[]): boolean => {
        let result = true;

        if (restrictions && restrictions.length > 0) {
            restrictions.forEach(w => {
                if (!!w.expirationDate && moment(w.expirationDate) < moment()) {
                    result = false;
                }
            });
        }

        return result;
    };


    /**
     * Pushes the provided restriction to the Medical Exam Certificate for saving.
     * If the restriction can have a waiver attached, it will additionally attach restrictionDetails regarding the waiver to the restriction for saving
     * If the restriction can have a exemption attached, it will additionally attach restrictionDetails regarding the exemption to the restriction for saving
     * @param certificate The Medical Exam Certificate to apply a restriction to
     * @param restrictionTypeId The restriction type id
     */
    private applyRestrictionToCertificate = (certificate: MedicalExamCertificate, restrictionTypeId: number): void => {
        const restriction = new MedicalExamCertificateRestriction();
        restriction.restrictionTypeId = restrictionTypeId;
        restriction.waivers = [];
        restriction.exemptions = [];
        // Set to true so that the service layer does not filter this out when saving restrictions
        // regardless of whether this is a waiver/exemption or non-waiver/exemption retriction, it must be active
        // because the list of restrictions that this function gets called on are restrictions selected from the dropdown
        // so, the intent has to be that they want the restriction to be active
        restriction.active = true;

        if (this.exemptionApplies(restrictionTypeId, certificate.medicalExamCertificateDecisionTypeId)) {
            this.applyDetailsToRestriction('exemption', restriction);
        } else if (this.waiverApplies(restrictionTypeId, certificate.medicalExamCertificateDecisionTypeId)) {
            this.applyDetailsToRestriction('waiver', restriction);
        }

        certificate.restrictions.push(restriction);
    };

    /**
     * Returns true if the combination of the selected decision and selected restriction is eligible for an exemption
     * @param restrictionTypeId The restriction type id
     * @param decisionTypeId The decision type id
     */
    private exemptionApplies = (restrictionTypeId: number, decisionTypeId: number): boolean => (
        restrictionTypeId == this.restrictionTypeEnum.AccompaniedbyWaiverExemption.id &&
        decisionTypeId == this.decisionTypeEnum.Qualified_NonExceptedInterstate.id
    );

    /**
     * Returns true if the combination of the selected decision and selected restriction is eligible for an exemption
     * @param restrictionTypeId The restriction type id
     * @param decisionTypeId The decision type id
     */
    private waiverApplies = (restrictionTypeId: number, decisionTypeId: number): boolean => restrictionTypeId == this.restrictionTypeEnum.QualifiedByOperationOf49CFR391_64.id || (
        restrictionTypeId == this.restrictionTypeEnum.AccompaniedbyWaiverExemption.id &&
        decisionTypeId == this.decisionTypeEnum.Qualified_StateVariances.id
    );

    /**
     * Attaches a waiver or exemption to a restriction from the form
     * @param indicator Whether the restrictionDetail is a waiver or exemption
     * @param restriction The restriction to apply the RestrictionDetail to
     */
    private applyDetailsToRestriction = (indicator: 'waiver' | 'exemption', restriction: MedicalExamCertificateRestriction): void => {
        const controls = (this.form.get(`${indicator}Details`) as UntypedFormArray).controls;
        if (!!controls && controls.length > 0) {
            controls.forEach(c => {
                if (true == c.get('active').value) {
                    const model = this.buildWaiverOrExemptionModel(indicator, c.value);
                    restriction[`${indicator}s`].push(model as any);
                    restriction.active = true;
                }
            });
        }
    };

    /**
     * Returns a waiver or exemption model that has all necessary properties populated
     * @param indicator Whether the restrictionDetail is a waiver or exemption
     * @param restrictionDetail The restrictionDetail
     */
    private buildWaiverOrExemptionModel = (indicator: 'waiver' | 'exemption', restrictionDetail: any): MedicalExamCertificateWaiver | MedicalExamCertificateExemption => {
        let model: MedicalExamCertificateWaiver | MedicalExamCertificateExemption;

        if (indicator === 'waiver') {
            model = new MedicalExamCertificateWaiver();
        } else {
            model = new MedicalExamCertificateExemption();
        }

        model[`${indicator}TypeId`] = restrictionDetail.id;
        model.effectiveDate = restrictionDetail.effectiveDate;
        model.expirationDate = restrictionDetail.expirationDate;
        return model;
    };

    // ====================================================================== Saving and Validation ======================================================================

    /**
     * Opens the Additional Requirements modal to create an additional requirements document
     */
    public createAdditionalRequirements = (): void => {
        const data = new AdditionalRequirementsModalComponentContext(this.customerNumberId, this.CDLMRPAdditionalRequirementsIncidentTypeId);
        this.modalService.open(AdditionalRequirementsModalComponent, data)
            .afterOpened()
            .subscribe(() => {
                if (this.dialogRef) {
                    this.dialogRef.close();
                }
            });
    };

    /**
     * Opens the modal to upload a document
     */
    public uploadDocument = (): void => {
        const data = new DocumentSelectModalContext(this.customerNumberId, null, null);
        this.modalService.open(DocumentSelectModalComponent, data, ModalSize.Large)
            .afterClosed()
            .pipe(filter(r => !!r))
            .subscribe(r => {
                this.documentFilename = r.documentFilename;
                this.form.get('documentId').setValue(r.documentId);
            });
    };

    // ====================================================================== Form Validators ======================================================================

    /**
     * Validates the the signature date is no greater than today.
     */
    private signatureDateValidator: ValidatorFn = (control) => {
        if (control.value && moment(control.value).toDate() > this.maximumSignatureDate) {
            return { signatureDateTooLarge: true };
        }

        return null;
    };

    /**
     * Validates that the expiration date is greater than or equal to the signature date,
     * and also within 2 years after the signature date.
     */
    private expirationDateValidator: ValidatorFn = (control) => {
        const expirationDate = moment(control.value).toDate();

        if (expirationDate < this.minimumExpirationDate && this.minimumExpirationDate !== null) {
            return { expirationDateTooSmall: true };
        }

        if (expirationDate > this.maximumExpirationDate && this.maximumExpirationDate !== null) {
            return { expirationDateTooLarge: true };
        }

        return null;
    };

    /**
     * Validates the registry code is a 10 digit number and not all repeating digits.
     */
    private registryCodeValidator: ValidatorFn = (control) => {
        if (control.value) {
            if (!/^\d{10}$/.test(control.value)) {
                return { invalidRegistryCode: true };
            }

            return this.repeatingDigitsValidator(control);
        }

        return null;
    };

    /**
     * Validates that not all characters are repeating.
     */
    private repeatingDigitsValidator: ValidatorFn = (control) => {
        if (control.value && /^(.)(\1+)$/.test(control.value)) {
            return { repeatingDigits: true };
        }

        return null;
    };

    /**
     * Validates the name only contains letters, numbers, hyphens, or apostrophes. No spaces!
     */
    private nameValidator: ValidatorFn = (control) => {
        if (!control.value || /^[a-zA-Z0-9-']+$/.test(control.value)) {
            return null;
        }

        return { invalidExaminerName: true };
    };
}
